import { nanoid } from 'nanoid';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import { getFirebaseAuth } from '@/shared/firebase/firebase';
import HttpClientWithRetry from '@/shared/httpclient/httpClientWithRetry';

export const PromptType = {
  SEMANTIC: 'Semantic',
  KEYWORDS: 'Keywords',
};

const backendHttpClient = new HttpClientWithRetry({
  serviceConfiguration: {
    baseUrl: process.env.VUE_APP_BACKEND_URL,
    requestTimeoutInMs: 20000,
  },
});
const assistantHttpClient = new HttpClientWithRetry({
  serviceConfiguration: {
    baseUrl: process.env.VUE_APP_ASSISTANT_URL,
    requestTimeoutInMs: 20000,
  },
});

const authenticateClient = async (client) => {
  const idToken = await getFirebaseAuth().currentUser.getIdToken(false);
  client.setHeaders({
    Accept: 'application/json',
    Authorization: `Bearer ${idToken}`,
  });
  return client;
};

export const getJournalSections = async (journalId) => {
  const client = await authenticateClient(backendHttpClient);
  const response = await client.get(`/recommendations/section/${journalId}`);
  const journal = response.data;

  if (journal.sections && journal.sections.length > 0) {
    return journal.sections;
  }

  return undefined;
};

export const updateJournalSection = async (
  searchIds,
  recommendationCounts,
  name,
  journalId,
) => {
  const strategies = searchIds.map((searchId, index) => ({
    recommendationCount: recommendationCounts[index],
    type: 'NOOS_ASSISTANT_SEARCH',
    useAnchor: false,
    searchId,
  }));
  const payload = {
    name,
    strategies,
    journalId,
  };
  const client = await authenticateClient(backendHttpClient);
  await client.put('/recommendations/section', payload);
};

export const createJournalSection = async (searchIds, recommendationCounts, name, reflectionId, headerId) => {
  const strategies = searchIds.map((searchId, index) => ({
    recommendationCount: recommendationCounts[index],
    type: 'NOOS_ASSISTANT_SEARCH',
    useAnchor: false,
    searchId,
  }));
  const payload = {
    name,
    reflectionId,
    strategies,
    headerId,
  };
  const client = await authenticateClient(backendHttpClient);
  await client.post('/recommendations/section', payload);
};


export class UrlAutocompleter {
  constructor() {
    this.clientPromise = authenticateClient(assistantHttpClient);
  }

  async getCompletion(string) {
    const client = await this.clientPromise;
    const encodedString = encodeURIComponent(string);

    const queryPath = `/autocomplete/url?q=${encodedString}`;
    try {
      const response = await client.get(queryPath);
      return await response.data;
    } catch (error) {
      throw Error('An error occurred while trying to call the Url Autocompleter');
    }
  }
}

export class RecommendationGenerator {
  constructor(maxFrequencyInSec, editorContentIndex, listener) {
    this.assistantSearch = new NoosAssistantSearch();
    this.editorContentIndex = editorContentIndex;
    this.listener = listener;
    this.generate = throttle(this.generateExecute, maxFrequencyInSec * 1000);
    this.lastId = '';
  }

  async generateExecute(cursorPosition) {
    const id = nanoid();
    this.lastId = id;
    const textPrompt = this.extractPrompt(cursorPosition);

    if (!textPrompt) {
      console.warn('No text prompt, skipping recommendation.');
      return;
    }

    const recommendations = await this.assistantSearch.fromTextPrompt(textPrompt, PromptType.SEMANTIC, 10);

    if (id === this.lastId) {
      this.listener.onNewRecommendation(recommendations);
    } else {
      console.warn('Not dispatching recommendations, newer versions are available.');
    }
  }

  extractPrompt(cursorPosition) {
    const element = this.editorContentIndex.getElementAt(cursorPosition);
    if (!element) {
      return undefined;
    }

    const sectionText = this.editorContentIndex.getSectionTextContent(element.section);
    if (sectionText === '') {
      return undefined;
    }


    let text = sectionText.trim();
    if (text[text.length - 1] !== '.'
      && text[text.length - 1] !== '!'
      && text[text.length - 1] !== '?'
    ) {
      text = `${text}...`;
    }

    const { headers } = element;
    if (headers.length === 0) {
      return text;
    }

    return `${headers.map(h => h.text).join(' - ')}: ${text}`;
  }
}

export class NoosAssistantSearch {
  async fetchResults(numberResultsOverride) {
    const client = await authenticateClient(assistantHttpClient);
    // TODO test this override
    if (typeof numberResultsOverride === 'number' && !Number.isNaN(numberResultsOverride)) {
      this.searchQuery.max_results = numberResultsOverride;
    }

    const response = await client.post(this.searchPath, this.searchQuery);
    return this.assembleItems(response.data);
  }

  fromTextPrompt(textPrompt, promptType = PromptType.SEMANTIC, numRecommendations) {
    const promptScoring = {};
    promptScoring[promptType] = {
      param: textPrompt,
      multiplier: 1.0,
    };
    this.searchPath = '/search/complex';
    this.searchQuery = {
      max_results: numRecommendations,
      scorings: [
        promptScoring,
        {
          DateDecay: { // Small date decay
            multiplier: 0.20,
            param: {
              half_life: {
                amount: 14,
                unit: 'd',
              },
              kind: 'Gaussian',
              from: {
                time: 1,
                unit: 'M',
              },
              to: {
                time: 0,
                unit: 'd',
              },
            },
          },
        },
      ],
      filters: [
        {
          Keywords: {
            kind: 'Include',
            param: textPrompt,
          },
        },
      ],
    };
    return this;
  }

  fromQuery(query) {
    this.searchPath = '/search/complex';
    this.searchQuery = query;
    return this;
  }

  fromDocument(documentDetails, max_results = 8) {
    const searchQuery = this.createSimilarQuery(documentDetails);
    searchQuery.filters = [];
    searchQuery.max_results = max_results; // eslint-disable-line

    this.searchPath = '/search/complex';
    this.searchQuery = searchQuery;
    return this;
  }

  createSimilarQuery(documentDetails) {
    if (documentDetails.analysisId) {
      // Analyses are not indexed by Noos Assistant, therefore a semantic search is required.
      return {
        scorings: [
          {
            Semantic: {
              param: `${documentDetails.title} ${documentDetails.excerpt}`,
              multiplier: 1.0,
            },
          },
        ],
      };
    }

    return {
      scorings: [
        {
          Document: {
            param: documentDetails.documentId,
            multiplier: 1.0,
          },
        },
      ],
    };
  }

  assembleItems(payload) {
    const getIsoString = (doc, path) => {
      const timestamp = get(doc, path, undefined);
      return timestamp ? new Date(timestamp).toISOString() : undefined;
    };

    const formattedAssistantDocs = {
      id: payload.id,
      total: payload.total,
      items: payload.items.map((doc) => ({
        documentId: doc.document.id,
        document: {
          ...doc.document,
          analysisId: doc.analysis_id,
          captureDate: getIsoString(doc, 'document.date_capture.timestamp'),
          datePublished: getIsoString(doc, 'document.date_published.timestamp'),
          excerpt: doc.document.description,
          leadImageUrl: doc.document.image_url,
          documentId: doc.document.id,
        },
        score: doc.score,
      })),
    };
    return formattedAssistantDocs;
  }

  async loadExistingSearch(searchId) {
    const client = await authenticateClient(assistantHttpClient);
    const baseUri = '/search/complex';
    const uri = `${baseUri}/${searchId}`;
    try {
      const response = await client.get(uri);
      return response.data;
    } catch (error) {
      console.error(error);
      throw Error(`An error occurred while trying to fetch search with id: ${searchId}\n ${error}`);
    }
  }

  extractWidgetParamsFromSavedSearch(savedSearchData, widgetId, keysToSkip) {
    const keys = Object.keys(savedSearchData);
    const widgetData = {};
    keys.forEach(key => {
      if (keysToSkip.includes(key)) return;
      const filteredElem = savedSearchData[key].filter(elem => {
        const elemKey = Object.keys(elem)[0];
        return elem[elemKey].id === widgetId;
      });
      if (filteredElem.length > 0) {
        widgetData[key] = filteredElem;
      }
    });
    return widgetData;
  }
}
